/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {Injectable, OpaqueToken} from '../di';
import {BaseError} from '../facade/errors';
import {stringify} from '../facade/lang';
import {ViewEncapsulation} from '../metadata';
import {Type} from '../type';

import {ComponentFactory} from './component_factory';
import {NgModuleFactory} from './ng_module_factory';
/**
 *  Indicates that a component is still being loaded in a synchronous compile.
  * *
 */
export class ComponentStillLoadingError extends BaseError {
/**
 * @param {?} compType
 */
constructor(public compType: Type<any>) {
    super(`Can't compile synchronously as ${stringify(compType)} is still being loaded!`);
  }
}

function ComponentStillLoadingError_tsickle_Closure_declarations() {
 /** @type {?} */
ComponentStillLoadingError.prototype.compType;
}

/**
 *  Combination of NgModuleFactory and ComponentFactorys.
  * *
 */
export class ModuleWithComponentFactories<T> {
/**
 * @param {?} ngModuleFactory
 * @param {?} componentFactories
 */
constructor(
public ngModuleFactory: NgModuleFactory<T>,
public componentFactories: ComponentFactory<any>[]) {}
}

function ModuleWithComponentFactories_tsickle_Closure_declarations() {
 /** @type {?} */
ModuleWithComponentFactories.prototype.ngModuleFactory;
 /** @type {?} */
ModuleWithComponentFactories.prototype.componentFactories;
}

/**
 * @return {?}
 */
function _throwError() {
  throw new Error(`Runtime compiler is not loaded`);
}
/**
 *  Low-level service for running the angular compiler during runtime
  * to create {@link ComponentFactory}s, which
  * can later be used to create and render a Component instance.
  * *
  * Each `@NgModule` provides an own `Compiler` to its injector,
  * that will use the directives/pipes of the ng module for compilation
  * of components.
 */
export class Compiler {
/**
 *  Compiles the given NgModule and all of its components. All templates of the components listed
  * in `entryComponents`
  * have to be inlined. Otherwise throws a {@link ComponentStillLoadingError}.
 * @param {?} moduleType
 * @return {?}
 */
compileModuleSync<T>(moduleType: Type<T>): NgModuleFactory<T> { throw _throwError(); }
/**
 *  Compiles the given NgModule and all of its components
 * @param {?} moduleType
 * @return {?}
 */
compileModuleAsync<T>(moduleType: Type<T>): Promise<NgModuleFactory<T>> { throw _throwError(); }
/**
 *  Same as {@link compileModuleSync} but also creates ComponentFactories for all components.
 * @param {?} moduleType
 * @return {?}
 */
compileModuleAndAllComponentsSync<T>(moduleType: Type<T>): ModuleWithComponentFactories<T> {
    throw _throwError();
  }
/**
 *  Same as {@link compileModuleAsync} but also creates ComponentFactories for all components.
 * @param {?} moduleType
 * @return {?}
 */
compileModuleAndAllComponentsAsync<T>(moduleType: Type<T>):
      Promise<ModuleWithComponentFactories<T>> {
    throw _throwError();
  }
/**
 *  Exposes the CSS-style selectors that have been used in `ngContent` directives within
  * the template of the given component.
  * This is used by the `upgrade` library to compile the appropriate transclude content
  * in the Angular 1 wrapper component.
 * @param {?} component
 * @return {?}
 */
getNgContentSelectors(component: Type<any>): string[] { throw _throwError(); }
/**
 *  Clears all caches.
 * @return {?}
 */
clearCache(): void {}
/**
 *  Clears the cache for the given component/ngModule.
 * @param {?} type
 * @return {?}
 */
clearCacheFor(type: Type<any>) {}
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

function Compiler_tsickle_Closure_declarations() {
 /** @type {?} */
Compiler.decorators;
 /**
  * @nocollapse
  * @type {?}
  */
Compiler.ctorParameters;
}


/**
 * Options for creating a compiler
 *
 * @experimental
 */
export type CompilerOptions = {
  useDebug?: boolean,
  useJit?: boolean,
  defaultEncapsulation?: ViewEncapsulation,
  providers?: any[],
};

/**
 * Token to provide CompilerOptions in the platform injector.
 *
 * @experimental
 */
export const /** @type {?} */ COMPILER_OPTIONS = new OpaqueToken('compilerOptions');
/**
 *  A factory for creating a Compiler
  * *
 * @abstract
 */
export abstract class CompilerFactory {
/**
 * @abstract
 * @param {?=} options
 * @return {?}
 */
createCompiler(options?: CompilerOptions[]) {}
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
